/*
 * Part catalogue for the 3D startup-loader engine (CFM LEAP-1B class).
 *
 * Geometry reuses the station/radius layout of the 2D drawing
 * (../engineGeometry.ts, "v1 units") converted to metres: fan tip radius
 * 190 units = 0.88 m, x measured from the fan leading edge (v1 x = 300).
 * Axis is +X, θ = 0 is top, θ = 90° faces the camera side (+Z).
 *
 * Every part carries its own choreography:
 *   build   - when it materialises during the first-play assembly and the
 *             offset it flies in from;
 *   explode - when it leaves during the exploded view and where it goes.
 * The collapse is derived from the explode (reverse order) in choreography.ts.
 *
 * Nothing here runs at import time: `getParts()` builds once, on demand, so
 * the geometry cost lands after the loader's first paint.
 */

import {
  BladeSpec, MeshBuilder, P2, bezier2, blade, bladeRing, box, cylinderX, lathe, orient, polar, torusX, tube,
} from '../gl/geometry';
import { Mat4, Vec3, deg, mat4, v3 } from '../gl/math';
import { HPC_STAGES, LPT_STAGES, HPT_STAGES, BOOSTER_STAGES, FAN_BLADES } from '../engineGeometry';

export type MaterialId =
  | 'metal' | 'metalDark' | 'hot' | 'gold' | 'copper' | 'composite' | 'livery' | 'harness' | 'flame';

export interface Offset {
  t?: Vec3;
  /** Rotation (rad) about the part's hinge line. */
  hinge?: number;
}

export interface Motion {
  at: number;
  dur: number;
}

export interface PartDef {
  id: string;
  mesh: MeshBuilder;
  material: MaterialId;
  /** Rotating parts spin about the axis with their spool. */
  spool?: 'lp' | 'hp';
  /** Placement for meshes shared between parts (fan blades, fuel nozzles). */
  base?: Mat4;
  /** Hinge line parallel to X through (0, y, z). */
  hinge?: [number, number];
  /** Tubes: grow along their path instead of dissolving in. */
  grow?: boolean;
  /** Small details dropped first on slow GPUs. */
  minor?: boolean;
  build: Motion & { from?: Offset };
  explode: Motion & { to: Offset };
}

/* ── units and helpers ────────────────────────────────────────────────── */

export const S = 0.88 / 190;
const X = (x: number) => (x - 300) * S;
const R = (r: number) => r * S;
const P = (pts: P2[]): P2[] => pts.map(([x, r]) => [X(x), R(r)]);
const rect = (x0: number, x1: number, r0: number, r1: number): P2[] => P([[x0, r0], [x1, r0], [x1, r1], [x0, r1]]);
const B = (s: BladeSpec): BladeSpec => ({
  ...s,
  x: X(s.x),
  rHub: R(s.rHub),
  rTip: R(s.rTip),
  chordHub: R(s.chordHub),
  chordTip: R(s.chordTip),
  sweep: s.sweep ? R(s.sweep) : 0,
  lean: s.lean ? R(s.lean) : 0,
});
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const radial = (th: number, d: number): Vec3 => [0, Math.cos(th) * d, Math.sin(th) * d];
/** Point at v1 (x, r) and angle θ, in metres. */
const at = (x: number, r: number, th: number): Vec3 => polar(X(x), R(r), th);

/** Cutaway: a wedge facing the camera side is removed from the cases. */
const CUT = { a0: deg(110), a1: deg(380) };
/** Split halves (HPC case): upper and lower, each minus its share of the wedge. */
const HALF_TOP = { a0: deg(-90), a1: deg(20) };
const HALF_BOT = { a0: deg(110), a1: deg(270) };
const inCut = (th: number) => {
  const a = ((th % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  return a > deg(20) && a < deg(110);
};

function disc(x: number, w: number, rRim: number, rBore: number, rimH = 4, web = 0.3, bore = 0.8, boreH?: number): P2[] {
  const c = x + w / 2;
  const ww = (w * web) / 2;
  const bw = (w * bore) / 2;
  const bh = boreH ?? Math.max(3, (rRim - rBore) * 0.22);
  const rw = rRim - rimH;
  return P([
    [x, rRim], [x + w, rRim], [x + w, rw], [c + ww, rw], [c + ww, rBore + bh],
    [c + bw, rBore + bh], [c + bw, rBore], [c - bw, rBore], [c - bw, rBore + bh],
    [c - ww, rBore + bh], [c - ww, rw], [x, rw],
  ]);
}

const bHub = (x: number) => lerp(70, 76, (x - 384) / 86);
const bTip = (x: number) => lerp(104, 97, (x - 384) / 86);
const cHub = (x: number) => lerp(54, 64, (x - 526) / 182);
const cTip = (x: number) => lerp(83, 70, (x - 526) / 182);
const tHub = (x: number) => lerp(66, 62, (x - 794) / 52);
const tTip = (x: number) => lerp(88, 92, (x - 794) / 52);
const lHub = (x: number) => lerp(62, 66, (x - 858) / 114);
const lTip = (x: number) => lerp(100, 120, (x - 858) / 114);

/* ── catalogue ────────────────────────────────────────────────────────── */

let cache: PartDef[] | null = null;

export function getParts(): PartDef[] {
  if (!cache) cache = buildParts();
  return cache;
}

function buildParts(): PartDef[] {
  const parts: PartDef[] = [];
  const add = (p: PartDef) => {
    parts.push(p);
    return p;
  };
  const still = (at: number, dur = 0.8) => ({ at, dur });

  /* Shafts and bearings: the core the build starts from. */
  add({
    id: 'lpShaft', material: 'metal', spool: 'lp',
    mesh: lathe(rect(304, 990, 12, 17), { closed: true, seg: 40 }),
    build: still(0), explode: { at: 13.6, dur: 1.2, to: { t: [0.25, 0, 0] } },
  });
  add({
    id: 'hpShaft', material: 'metal', spool: 'hp',
    mesh: lathe(rect(514, 846, 28, 34), { closed: true, seg: 48 }),
    build: still(0.05), explode: { at: 13.6, dur: 1.2, to: { t: [-0.1, 0, 0] } },
  });
  const bearings: [number, number, number, number][] = [
    [346, 356, 17, 24], [368, 378, 17, 24], [516, 526, 34, 41], [848, 856, 17, 28], [985, 993, 17, 26],
  ];
  bearings.forEach(([x0, x1, r0, r1], k) => {
    const m = new MeshBuilder();
    const t = (r1 - r0) * 0.25;
    lathe(rect(x0, x1, r0, r0 + t), { closed: true, seg: 40 }, m);
    lathe(rect(x0, x1, r1 - t, r1), { closed: true, seg: 40 }, m);
    const rm = R((r0 + r1) / 2);
    const rr = R((r1 - r0) * 0.22);
    for (let i = 0; i < 14; i++) {
      const th = (2 * Math.PI * i) / 14;
      cylinderX(X(x0 + 1.5), X(x1 - 1.5), rr, 6, m, mat4.translation(0, rm * Math.cos(th), rm * Math.sin(th)));
    }
    add({
      id: `bearing${k}`, material: 'metal',
      mesh: m, build: still(0.1 + k * 0.06), explode: { at: 13.0 + k * 0.05, dur: 1.2, to: { t: [0, 0.24, 0] } },
    });
  });
  add({
    id: 'sumps', material: 'metalDark',
    mesh: (() => {
      const m = new MeshBuilder();
      lathe(P([[356, 24], [470, 66], [472, 70], [356, 27]]), { closed: true, ...CUT, seg: 64 }, m);
      lathe(P([[378, 24], [472, 62], [472, 64], [378, 26]]), { closed: true, ...CUT, seg: 64 }, m);
      lathe(P([[986, 26], [990, 61], [994, 61], [992, 26]]), { closed: true, ...CUT, seg: 64 }, m);
      return m;
    })(),
    build: still(0.3), explode: { at: 13.2, dur: 1.2, to: { t: [0, -0.2, 0] } },
  });

  /* HP compressor: 10 blisk stages + vane rows; the first half is already on. */
  const pitch = (700 - 537) / (HPC_STAGES - 1);
  for (let k = 0; k < HPC_STAGES; k++) {
    const xr = 537 + k * pitch;
    const cr = 8 - 0.25 * k;
    const xs = xr + cr + 1.8;
    const cs = 6 - 0.15 * k;
    const stage = new MeshBuilder();
    lathe(disc(xr - 0.5, cr + 1, cHub(xr) - 0.3, 38, 3.5, 0.38, 0.95, 3), { closed: true, seg: 40 }, stage);
    lathe(P([[xr - 0.5, cHub(xr) - 0.3], [xr + cr + 0.5, cHub(xr) - 0.3], [xr + cr + 0.5, cHub(xr) + 0.8], [xr - 0.5, cHub(xr) + 0.8]]), { closed: true, seg: 40 }, stage);
    if (k < HPC_STAGES - 1) {
      const nx = 537 + (k + 1) * pitch - 0.5;
      const h = cHub((xr + nx) / 2);
      lathe(rect(xr + cr + 0.5, nx, h - 4.5, h - 3), { closed: true, seg: 40 }, stage);
    }
    const betaR = deg(38);
    bladeRing(B({
      x: xr, rHub: cHub(xr) + 0.8, rTip: cTip(xr) - 1, chordHub: cr / Math.cos(betaR), chordTip: cr / Math.cos(deg(50)),
      staggerHub: betaR, staggerTip: deg(50), thick: 0.07, camber: 0.05, spanSeg: 2, chordSeg: 3,
    }), 30 - k, 0, stage);
    const early = k < 5;
    add({
      id: `hpcStage${k}`, material: 'metal', spool: 'hp', mesh: stage,
      build: early ? still(0.15 + k * 0.06) : { at: 0.7 + (k - 5) * 0.22, dur: 0.7, from: { t: [0.55, 0, 0] } },
      explode: { at: 12.3 + k * 0.04, dur: 1.4, to: { t: [-0.34 + k * 0.064, 0, 0] } },
    });

    const vane = new MeshBuilder();
    const betaS = deg(-40);
    bladeRing(B({
      x: xs, rHub: cHub(xs) + 1, rTip: cTip(xs), chordHub: cs / Math.cos(betaS), chordTip: cs / Math.cos(betaS),
      staggerHub: betaS, staggerTip: deg(-34), thick: 0.06, camber: -0.04, spanSeg: 2, chordSeg: 3,
    }), 40 - k, 0.07, vane);
    lathe(rect(xs - 0.3, xs + cs + 0.3, cHub(xs) - 1.5, cHub(xs) + 1), { closed: true, seg: 40 }, vane);
    add({
      id: `hpcVane${k}`, material: 'metalDark', mesh: vane,
      build: early ? still(0.2 + k * 0.06) : { at: 0.8 + (k - 5) * 0.22, dur: 0.7, from: { t: [0.55, 0, 0] } },
      explode: { at: 12.32 + k * 0.04, dur: 1.4, to: { t: [-0.34 + k * 0.064 + 0.032, 0, 0] } },
    });
  }

  /* HPC case: two halves with VSV unison rings and levers. */
  const hpcHalf = (arc: { a0: number; a1: number }) => {
    const m = new MeshBuilder();
    lathe(P([[524, cTip(524)], [710, cTip(710)], [710, cTip(710) + 5], [524, cTip(524) + 5]]), { closed: true, seg: 96, ...arc }, m);
    [524, 600, 708].forEach(x => lathe(rect(x - 2, x + 2, cTip(x) + 5, cTip(x) + 10), { closed: true, seg: 96, ...arc }, m));
    for (let k = 0; k < 4; k++) {
      const xs = k === 0 ? 530 : 537 + (k - 1) * pitch + 8 + 1.8 + 3;
      const r = cTip(xs) + 12;
      torusX(X(xs), R(r), R(1.2), { seg: 80, minor: 6, ...arc }, m);
      const n = 26;
      for (let i = 0; i < n; i++) {
        const th = arc.a0 + ((arc.a1 - arc.a0) * (i + 0.5)) / n;
        const base = mat4.multiply(mat4.rotationX(th), mat4.translation(X(xs), R(cTip(xs) + 8.5), 0));
        box(R(1.6), R(7), R(1.6), m, base);
        box(R(4.5), R(1.2), R(1.6), m, mat4.multiply(base, mat4.translation(R(2), R(3.5), 0)));
      }
    }
    return m;
  };
  add({
    id: 'hpcCaseTop', material: 'metal', mesh: hpcHalf(HALF_TOP),
    build: { at: 3.1, dur: 0.8, from: { t: [0, 0.55, 0] } },
    explode: { at: 11.9, dur: 1.3, to: { t: [0, 0.78, 0] } },
  });
  add({
    id: 'hpcCaseBottom', material: 'metal', mesh: hpcHalf(HALF_BOT),
    build: { at: 3.15, dur: 0.8, from: { t: [0, -0.55, 0] } },
    explode: { at: 11.95, dur: 1.3, to: { t: [0, -0.78, 0] } },
  });

  /* Combustor: casings, gold annular liner, flame, 20 fuel nozzles, manifold. */
  add({
    id: 'combInner', material: 'metalDark',
    mesh: lathe(P([[708, 58], [722, 56], [794, 58], [794, 53], [722, 51], [708, 53]]), { closed: true, seg: 72, ...CUT }),
    build: { at: 1.2, dur: 0.7, from: { t: [0.4, 0, 0] } },
    explode: { at: 12.45, dur: 1.3, to: { t: [0.26, -0.3, 0] } },
  });
  const liner = new MeshBuilder();
  lathe(P([[728, 93], [790, 88], [790, 86.5], [728, 91.5]]), { closed: true, seg: 96, ...CUT }, liner);
  lathe(P([[728, 65], [790, 67.5], [790, 69], [728, 66.5]]), { closed: true, seg: 96, ...CUT }, liner);
  lathe(rect(726, 729, 66.5, 75), { closed: true, seg: 96, ...CUT }, liner);
  lathe(rect(726, 729, 83, 91.5), { closed: true, seg: 96, ...CUT }, liner);
  for (let i = 0; i < 20; i++) {
    const th = (2 * Math.PI * i) / 20;
    if (inCut(th)) continue;
    torusX(X(727.5), R(79), R(3.4), { seg: 10, minor: 6, a0: th - 0.16, a1: th + 0.16 }, liner);
  }
  add({
    id: 'liner', material: 'gold', mesh: liner,
    build: { at: 1.4, dur: 0.8, from: { t: [0.45, 0, 0] } },
    explode: { at: 12.4, dur: 1.4, to: { t: [0.34, 0, 0] } },
  });
  const holes = new MeshBuilder();
  [748, 766].forEach(x => {
    const rOut = lerp(92.3, 88, (x - 728) / 62);
    const rIn = lerp(65.8, 68.2, (x - 728) / 62);
    for (let i = 0; i < 36; i++) {
      const th = (2 * Math.PI * (i + 0.5)) / 36;
      if (inCut(th)) continue;
      for (const r of [rOut, rIn]) {
        const m = mat4.multiply(mat4.rotationX(th), mat4.multiply(mat4.translation(X(x), R(r), 0), mat4.rotationZ(Math.PI / 2)));
        cylinderX(-R(1.2), R(1.2), R(1.5), 8, holes, m);
      }
    }
  });
  add({
    id: 'linerHoles', material: 'metalDark', mesh: holes, minor: true,
    build: { at: 1.5, dur: 0.8, from: { t: [0.45, 0, 0] } },
    explode: { at: 12.4, dur: 1.4, to: { t: [0.34, 0, 0] } },
  });
  const flameProfile: P2[] = [];
  for (let i = 0; i < 16; i++) {
    const a = (2 * Math.PI * i) / 16;
    flameProfile.push([758 + 27 * Math.cos(a), 79 + 5.5 * Math.sin(a) * (0.6 + 0.4 * Math.cos(a))]);
  }
  add({
    id: 'flame', material: 'flame', mesh: lathe(P(flameProfile), { closed: true, seg: 96, sharp: 80, edges: false, ...CUT }),
    build: { at: 1.9, dur: 0.9 }, explode: { at: 12.4, dur: 1.4, to: { t: [0.34, 0, 0] } },
  });
  const outerCase = new MeshBuilder();
  lathe(P([[708, 76], [714, 76], [730, 98], [794, 103], [794, 108], [728, 103], [708, 82]]), { closed: true, seg: 96, ...CUT }, outerCase);
  lathe(rect(792, 796, 103, 113), { closed: true, seg: 96, ...CUT }, outerCase);
  for (let i = 0; i < 20; i++) {
    const th = (2 * Math.PI * i) / 20;
    if (inCut(th)) continue;
    const m = mat4.multiply(mat4.rotationX(th), mat4.multiply(mat4.translation(X(721), R(106), 0), mat4.rotationZ(Math.PI / 2)));
    cylinderX(0, R(8), R(4.5), 10, outerCase, m);
  }
  add({
    id: 'combOuter', material: 'metal', mesh: outerCase,
    build: { at: 2.8, dur: 0.8, from: { t: [0.5, 0, 0] } },
    explode: { at: 12.35, dur: 1.4, to: { t: [0.22, 0.6, 0] } },
  });
  const nozzle = new MeshBuilder();
  box(R(4), R(29), R(3.5), nozzle, mat4.translation(X(721), R(97.5), 0));
  cylinderX(X(721), X(730), R(3.2), 12, nozzle, mat4.translation(0, R(79), 0));
  box(R(9), R(3), R(9), nozzle, mat4.translation(X(721), R(114), 0));
  cylinderX(X(716), X(722), R(1.8), 8, nozzle, mat4.translation(0, R(117), 0));
  for (let i = 0; i < 20; i++) {
    const th = (2 * Math.PI * i) / 20;
    add({
      id: `nozzle${i}`, material: 'metal', mesh: nozzle, base: mat4.rotationX(th),
      build: { at: 2.3 + i * 0.03, dur: 0.5, from: { t: radial(th, 0.35) } },
      explode: { at: 12.5 + i * 0.012, dur: 1.2, to: { t: v3.add(radial(th, 0.42), [0.3, 0, 0]) } },
    });
  }
  const manifold = new MeshBuilder();
  torusX(X(716), R(119), R(1.8), { seg: 120, minor: 8 }, manifold);
  add({
    id: 'manifold', material: 'copper', mesh: manifold,
    build: { at: 3.3, dur: 0.6, from: { t: [-0.3, 0, 0] } },
    explode: { at: 12.3, dur: 1.4, to: { t: [0.75, 0, 0] } },
  });
  const igniters = new MeshBuilder();
  [deg(150), deg(210)].forEach(th => {
    const m = mat4.multiply(mat4.rotationX(th), mat4.multiply(mat4.translation(X(753.5), R(88), 0), mat4.rotationZ(Math.PI / 2)));
    cylinderX(0, R(28), R(1.8), 10, igniters, m);
    cylinderX(R(18), R(28), R(3), 10, igniters, m);
  });
  add({
    id: 'igniters', material: 'metalDark', mesh: igniters, minor: true,
    build: { at: 3.0, dur: 0.5, from: { t: [0, -0.2, 0] } },
    explode: { at: 12.6, dur: 1.2, to: { t: [0.2, -0.6, 0] } },
  });

  /* HP turbine: 2 stages of shrouded, fir-tree-rooted blades + NGVs. */
  const hptSpan = 50 / HPT_STAGES;
  for (let k = 0; k < HPT_STAGES; k++) {
    const xv = 796 + k * hptSpan;
    const xr = xv + 12;
    const cv = 9 - k;
    const cr = 9 - k;
    const ngv = new MeshBuilder();
    bladeRing(B({
      x: xv, rHub: tHub(xv), rTip: tTip(xv), chordHub: cv * 1.3, chordTip: cv * 1.3,
      staggerHub: deg(-45), staggerTip: deg(-42), thick: 0.14, camber: -0.1, spanSeg: 2, chordSeg: 5,
    }), 34 + 6 * k, 0, ngv);
    lathe(rect(xv - 1, xv + cv + 1, tHub(xv) - 2, tHub(xv)), { closed: true, seg: 72 }, ngv);
    lathe(rect(xv - 1, xv + cv + 1, tTip(xv), tTip(xv) + 2), { closed: true, seg: 72 }, ngv);
    add({
      id: `hptNgv${k}`, material: 'hot', mesh: ngv,
      build: { at: 1.5 + k * 0.2, dur: 0.7, from: { t: [0.8, 0, 0] } },
      explode: { at: 12.8 + k * 0.06, dur: 1.4, to: { t: [0.5 + k * 0.13, 0, 0] } },
    });
    const st = new MeshBuilder();
    lathe(disc(xr - 3, cr + 6, tHub(xr) - 5.5, 36, 4, 0.3, 0.9, 5), { closed: true, seg: 44 }, st);
    lathe(rect(xr - 0.5, xr + cr + 0.5, tHub(xr) - 5.5, tHub(xr)), { closed: true, seg: 44 }, st);
    bladeRing(B({
      x: xr, rHub: tHub(xr), rTip: tTip(xr) - 1, chordHub: cr * 1.35, chordTip: cr * 1.3,
      staggerHub: deg(40), staggerTip: deg(48), thick: 0.16, camber: 0.12, spanSeg: 2, chordSeg: 5,
    }), 56, 0, st);
    add({
      id: `hptStage${k}`, material: 'hot', spool: 'hp', mesh: st,
      build: { at: 1.6 + k * 0.2, dur: 0.7, from: { t: [0.8, 0, 0] } },
      explode: { at: 12.82 + k * 0.06, dur: 1.4, to: { t: [0.56 + k * 0.13, 0, 0] } },
    });
  }
  add({
    id: 'hptCase', material: 'metal',
    mesh: (() => {
      const m = new MeshBuilder();
      lathe(P([[794, tTip(794) + 4], [846, tTip(846) + 4], [846, tTip(846) + 9], [794, tTip(794) + 9]]), { closed: true, seg: 96, ...CUT }, m);
      lathe(rect(792, 797, tTip(794) + 9, tTip(794) + 14), { closed: true, seg: 96, ...CUT }, m);
      return m;
    })(),
    build: { at: 3.4, dur: 0.7, from: { t: [0.5, 0, 0] } },
    explode: { at: 12.75, dur: 1.4, to: { t: [0.62, 0.5, 0] } },
  });

  /* LP turbine: 5 shrouded stages + vanes, and its case. */
  const lptPitch = 112 / LPT_STAGES;
  for (let k = 0; k < LPT_STAGES; k++) {
    const xv = 860 + k * lptPitch;
    const cv = Math.min(9, lptPitch * 0.4);
    const xr = xv + cv + 1.5;
    const cr = Math.min(9.5, lptPitch * 0.42);
    const ngv = new MeshBuilder();
    if (k === 0) {
      bladeRing(B({ x: 847, rHub: 62, rTip: 95, chordHub: 11, chordTip: 12, staggerHub: deg(-10), staggerTip: deg(-12), thick: 0.14, camber: -0.03, spanSeg: 2, chordSeg: 4 }), 16, 0, ngv);
    }
    bladeRing(B({
      x: xv, rHub: lHub(xv) + 1, rTip: lTip(xv), chordHub: cv * 1.25, chordTip: cv * 1.25,
      staggerHub: deg(-38), staggerTip: deg(-34), thick: 0.1, camber: -0.08, spanSeg: 2, chordSeg: 4,
    }), 52, 0, ngv);
    lathe(rect(xv - 0.5, xv + cv + 0.5, lHub(xv) - 1, lHub(xv) + 1), { closed: true, seg: 72 }, ngv);
    add({
      id: `lptNgv${k}`, material: 'hot', mesh: ngv,
      build: { at: 1.95 + k * 0.14, dur: 0.7, from: { t: [1.1, 0, 0] } },
      explode: { at: 13.0 + k * 0.05, dur: 1.4, to: { t: [0.95 + k * 0.13, 0, 0] } },
    });
    const st = new MeshBuilder();
    const dx = xr - 1;
    lathe(disc(dx, cr + 2, lHub(dx) - 0.5, 24, 4, 0.26, 0.8, 4), { closed: true, seg: 44 }, st);
    lathe(rect(xr - 0.5, xr + cr + 1.5, lTip(xr) - 2.5, lTip(xr) - 1), { closed: true, seg: 72 }, st);
    if (k < LPT_STAGES - 1) {
      const nx = 860 + (k + 1) * lptPitch + cv + 1.5 - 1;
      const h = lHub((dx + nx) / 2);
      lathe(rect(dx + cr + 2, nx, h - 4.5, h - 3), { closed: true, seg: 44 }, st);
    }
    bladeRing(B({
      x: xr, rHub: lHub(xr), rTip: lTip(xr) - 2.5, chordHub: cr * 1.3, chordTip: cr * 1.2,
      staggerHub: deg(35), staggerTip: deg(52), thick: 0.1, camber: 0.1, spanSeg: 2, chordSeg: 4,
    }), 48, 0, st);
    add({
      id: `lptStage${k}`, material: 'hot', spool: 'lp', mesh: st,
      build: { at: 2.0 + k * 0.14, dur: 0.7, from: { t: [1.1, 0, 0] } },
      explode: { at: 13.02 + k * 0.05, dur: 1.4, to: { t: [1.01 + k * 0.13, 0, 0] } },
    });
  }
  add({
    id: 'lptCase', material: 'metal',
    mesh: (() => {
      const m = new MeshBuilder();
      lathe(P([[846, 97], [974, lTip(974)], [974, lTip(974) + 5], [846, 102]]), { closed: true, seg: 96, ...CUT }, m);
      [846, 910, 972].forEach(x => {
        const r = lerp(102, lTip(974) + 5, (x - 846) / 128);
        lathe(rect(x - 2, x + 2, r, r + 5), { closed: true, seg: 96, ...CUT }, m);
      });
      return m;
    })(),
    build: { at: 3.5, dur: 0.8, from: { t: [0.9, 0, 0] } },
    explode: { at: 13.1, dur: 1.4, to: { t: [1.3, 0.7, 0] } },
  });

  /* Turbine rear frame, core nozzle, plug. */
  const trf = new MeshBuilder();
  lathe(P([[972, 120], [1002, 123], [1002, 128], [972, 125]]), { closed: true, seg: 96, ...CUT }, trf);
  lathe(rect(972, 1002, 61, 66), { closed: true, seg: 72 }, trf);
  bladeRing(B({ x: 976, rHub: 66, rTip: 120.5, chordHub: 22, chordTip: 20, staggerHub: deg(-8), staggerTip: deg(-12), thick: 0.16, camber: 0, spanSeg: 3, chordSeg: 5 }), 12, 0, trf);
  add({
    id: 'trf', material: 'metal', mesh: trf,
    build: { at: 3.7, dur: 0.8, from: { t: [1.2, 0, 0] } },
    explode: { at: 13.4, dur: 1.3, to: { t: [1.85, 0, 0] } },
  });
  add({
    id: 'coreNozzle', material: 'metal',
    mesh: lathe(P([[1002, 123], [1070, 110], [1070, 113], [1002, 127]]), { closed: true, seg: 96, ...CUT }),
    build: { at: 3.9, dur: 0.8, from: { t: [1.5, 0, 0] } },
    explode: { at: 13.5, dur: 1.3, to: { t: [2.15, 0, 0] } },
  });
  add({
    id: 'plug', material: 'metalDark',
    mesh: lathe(P([[1000, 44], ...bezier2([1002, 66], [1040, 64], [1086, 32], [1118, 0], 14)]), { seg: 64, sharp: 40 }),
    build: { at: 4.0, dur: 0.8, from: { t: [1.8, 0, 0] } },
    explode: { at: 13.55, dur: 1.3, to: { t: [2.5, 0, 0] } },
  });

  /* Booster, splitter, fan hub frame. */
  const boost = new MeshBuilder();
  lathe(P([[382, bHub(382) - 1], [454, bHub(454) - 1], [454, bHub(454) - 6], [382, bHub(382) - 6]]), { closed: true, seg: 72 }, boost);
  lathe(P([[342, 45], [386, bHub(386) - 1], [386, bHub(386) - 6], [342, 39]]), { closed: true, seg: 72 }, boost);
  const boostVanes = new MeshBuilder();
  for (let k = 0; k < BOOSTER_STAGES; k++) {
    const xr = 384 + k * 28;
    const cr = 12 - k;
    const xs = xr + 16;
    bladeRing(B({ x: xr, rHub: bHub(xr), rTip: bTip(xr) - 1.2, chordHub: cr * 1.3, chordTip: cr * 1.45, staggerHub: deg(40), staggerTip: deg(52), thick: 0.07, camber: 0.05, spanSeg: 2, chordSeg: 4 }), 44, 0, boost);
    bladeRing(B({ x: xs, rHub: bHub(xs) + 1.2, rTip: bTip(xs), chordHub: 9 * 1.3, chordTip: 9 * 1.3, staggerHub: deg(-38), staggerTip: deg(-32), thick: 0.06, camber: -0.04, spanSeg: 2, chordSeg: 4 }), 52, 0, boostVanes);
  }
  add({
    id: 'boosterRotor', material: 'metal', spool: 'lp', mesh: boost,
    build: { at: 3.4, dur: 0.7, from: { t: [-0.6, 0, 0] } },
    explode: { at: 11.6, dur: 1.4, to: { t: [-0.62, 0, 0] } },
  });
  add({
    id: 'boosterVanes', material: 'metalDark', mesh: boostVanes,
    build: { at: 3.5, dur: 0.7, from: { t: [-0.6, 0, 0] } },
    explode: { at: 11.55, dur: 1.4, to: { t: [-0.8, 0, 0] } },
  });
  const splitter = new MeshBuilder();
  lathe(P([[376, 106], ...bezier2([382, 111], [390, 113], [396, 113.5], [400, 114], 4), [472, 120], [472, bTip(472)], [384, bTip(384)], [378, 104]]), { closed: true, seg: 96, ...CUT }, splitter);
  add({
    id: 'boosterCase', material: 'metal', mesh: splitter,
    build: { at: 3.7, dur: 0.7, from: { t: [-0.6, 0, 0] } },
    explode: { at: 11.5, dur: 1.4, to: { t: [-1.0, 0.25, 0] } },
  });
  const frame = new MeshBuilder();
  const duct = (top: boolean) => {
    const a = top ? 97 : 76, b = top ? 83 : 54, t = top ? 6 : -6;
    const outer = bezier2([472, a], [496, a], [506, b], [528, b], 8);
    const inner = bezier2([528, b + t], [506, b + t], [496, a + t], [472, a + t], 8);
    return P([...outer, ...inner]);
  };
  lathe(duct(true), { closed: true, seg: 96, ...CUT }, frame);
  lathe(duct(false), { closed: true, seg: 96, ...CUT }, frame);
  bladeRing(B({ x: 480, rHub: 70, rTip: 96, chordHub: 26, chordTip: 22, staggerHub: 0, staggerTip: 0, thick: 0.16, camber: 0, spanSeg: 3, chordSeg: 5, sweep: 4 }), 10, deg(18), frame);
  add({
    id: 'fanFrame', material: 'metalDark', mesh: frame,
    build: { at: 3.6, dur: 0.7, from: { t: [-0.5, 0, 0] } },
    explode: { at: 11.7, dur: 1.4, to: { t: [-0.42, 0, 0] } },
  });

  /* Fan: disc, 18 swept wide-chord blades, spinner, OGVs, fan case. */
  const fanDisc = new MeshBuilder();
  lathe(disc(300, 46, 45, 20, 6, 0.22, 0.72), { closed: true, seg: 72 }, fanDisc);
  lathe(rect(296, 351, 56, 60), { closed: true, seg: 96 }, fanDisc);
  lathe(rect(304, 340, 17, 20), { closed: true, seg: 40 }, fanDisc);
  add({
    id: 'fanDisc', material: 'metal', spool: 'lp', mesh: fanDisc,
    build: { at: 4.0, dur: 0.7, from: { t: [-0.9, 0, 0] } },
    explode: { at: 11.3, dur: 1.5, to: { t: [-2.3, 0, 0] } },
  });
  const fanBlade = blade(B({
    x: 298, rHub: 60, rTip: 188.5, chordHub: 58, chordTip: 92, staggerHub: deg(28), staggerTip: deg(60),
    thick: 0.06, camber: 0.05, sweep: -16, lean: 6, spanSeg: 12, chordSeg: 10,
  }));
  for (let k = 0; k < FAN_BLADES; k++) {
    const th = (2 * Math.PI * k) / FAN_BLADES;
    add({
      id: `fanBlade${k}`, material: 'metal', spool: 'lp', mesh: fanBlade, base: mat4.rotationX(th),
      build: { at: 4.4 + k * 0.055, dur: 0.5, from: { t: v3.add([-0.45, 0, 0], radial(th, 0.03)) } },
      explode: { at: 11.35 + k * 0.02, dur: 1.5, to: { t: v3.add([-2.3, 0, 0], radial(th, 0.5)) } },
    });
  }
  const spinner = new MeshBuilder();
  lathe(P([...bezier2([236, 0], [240, 28], [270, 54], [300, 58], 14), [300, 44]]), { seg: 72, sharp: 40 }, spinner);
  for (let i = 0; i < 18; i++) {
    const th = (2 * Math.PI * (i + 0.5)) / 18;
    cylinderX(X(296), X(300.5), R(1.6), 8, spinner, mat4.translation(0, R(52) * Math.cos(th), R(52) * Math.sin(th)));
  }
  add({
    id: 'spinner', material: 'metal', spool: 'lp', mesh: spinner,
    build: { at: 5.5, dur: 0.6, from: { t: [-0.6, 0, 0] } },
    explode: { at: 11.25, dur: 1.5, to: { t: [-2.95, 0, 0] } },
  });
  add({
    id: 'ogv', material: 'metalDark',
    mesh: bladeRing(B({ x: 408, rHub: 115, rTip: 191.5, chordHub: 20, chordTip: 26, staggerHub: deg(-14), staggerTip: deg(-20), thick: 0.08, camber: -0.05, sweep: 10, spanSeg: 4, chordSeg: 5 }), 44),
    build: { at: 4.3, dur: 0.7, from: { t: [-0.6, 0, 0] } },
    explode: { at: 11.45, dur: 1.4, to: { t: [-1.4, 0, 0] } },
  });
  const fanCase = new MeshBuilder();
  lathe(P([[266, 195], [394, 195], [394, 201], [360, 202], [354, 214], [292, 214], [286, 202], [266, 201]]), { closed: true, seg: 96, ...CUT }, fanCase);
  lathe(rect(264, 270, 195, 219), { closed: true, seg: 96, ...CUT }, fanCase);
  lathe(rect(390, 396, 195, 213), { closed: true, seg: 96, ...CUT }, fanCase);
  lathe(rect(396, 448, 192, 197), { closed: true, seg: 96, ...CUT }, fanCase);
  [310, 336].forEach(x => lathe(rect(x - 1.5, x + 1.5, 214, 218), { closed: true, seg: 96, ...CUT }, fanCase));
  add({
    id: 'fanCase', material: 'metal', mesh: fanCase,
    build: { at: 5.0, dur: 0.8, from: { t: [-1.0, 0, 0] } },
    explode: { at: 11.2, dur: 1.5, to: { t: [-1.75, 0.15, 0] } },
  });
  add({
    id: 'fanLiner', material: 'metalDark',
    mesh: lathe(rect(268, 392, 191, 195), { closed: true, seg: 96, ...CUT }),
    build: { at: 5.05, dur: 0.8, from: { t: [-1.0, 0, 0] } },
    explode: { at: 11.2, dur: 1.5, to: { t: [-1.75, 0.15, 0] } },
  });

  /* Accessory gearbox + LRUs, radial drive, FADEC. */
  const bottom = deg(180);
  const agb = new MeshBuilder();
  lathe(rect(392, 470, 222, 238), { closed: true, seg: 180, a0: deg(158), a1: deg(202) }, agb);
  [deg(166), deg(176), deg(186), deg(196)].forEach(th => {
    cylinderX(X(386), X(392), R(6), 16, agb, mat4.translation(0, R(230) * Math.cos(th), R(230) * Math.sin(th)));
  });
  add({
    id: 'agb', material: 'metalDark', mesh: agb,
    build: { at: 5.5, dur: 0.7, from: { t: [0, -0.6, 0] } },
    explode: { at: 10.9, dur: 1.3, to: { t: [0.2, -1.05, 0.2] } },
  });
  const lru = (th: number, build: (m: MeshBuilder, y: number, z: number) => void) => {
    const m = new MeshBuilder();
    build(m, R(230) * Math.cos(th), R(230) * Math.sin(th));
    return m;
  };
  const lrus: [string, number, (m: MeshBuilder, y: number, z: number) => void][] = [
    ['starter', deg(166), (m, y, z) => {
      cylinderX(X(356), X(386), R(11), 24, m, mat4.translation(0, y, z));
      cylinderX(X(348), X(356), R(7), 20, m, mat4.translation(0, y, z));
      cylinderX(X(362), X(364), R(12.5), 24, m, mat4.translation(0, y, z));
    }],
    ['idg', deg(186), (m, y, z) => {
      cylinderX(X(346), X(386), R(14), 28, m, mat4.translation(0, y, z));
      box(R(20), R(10), R(18), m, mat4.translation(X(368), y - R(14), z));
      cylinderX(X(372), X(374), R(15.5), 28, m, mat4.translation(0, y, z));
    }],
    ['fuelPump', deg(196), (m, y, z) => {
      box(R(26), R(18), R(16), m, mat4.translation(X(373), y, z));
      cylinderX(X(356), X(362), R(6), 16, m, mat4.translation(0, y + R(4), z));
    }],
    ['lubeUnit', deg(176), (m, y, z) => {
      box(R(22), R(14), R(12), m, mat4.translation(X(375), y, z));
      cylinderX(X(362), X(366), R(5), 14, m, mat4.translation(0, y - R(3), z));
    }],
    ['hydPump', deg(156), (m, y, z) => {
      cylinderX(X(366), X(386), R(7), 18, m, mat4.translation(0, y, z));
    }],
  ];
  lrus.forEach(([id, th, fn], k) => {
    add({
      id, material: id === 'idg' || id === 'starter' ? 'metal' : 'metalDark', mesh: lru(th, fn),
      build: { at: 5.8 + k * 0.12, dur: 0.5, from: { t: v3.add(radial(th, 0.35), [-0.15, 0, 0]) } },
      explode: { at: 11.0 + k * 0.08, dur: 1.3, to: { t: v3.add(radial(th, 0.95), [-0.7 + k * 0.28, 0, 0]) } },
    });
  });
  const drive = new MeshBuilder();
  const d0 = at(505, 46, bottom), d1 = at(470, 214, bottom);
  cylinderX(0, v3.len(v3.sub(d1, d0)), R(3), 12, drive, orient(d0, v3.sub(d1, d0)));
  box(R(14), R(10), R(12), drive, mat4.translation(X(508), -R(42), 0));
  add({
    id: 'radialDrive', material: 'metal', mesh: drive,
    build: { at: 5.3, dur: 0.5, from: { t: [0, -0.2, 0] } },
    explode: { at: 11.0, dur: 1.3, to: { t: [0, -0.6, 0] } },
  });
  const fadecTh = deg(128);
  const fadec = new MeshBuilder();
  const fb = (m: Mat4) => mat4.multiply(mat4.rotationX(fadecTh), m);
  box(R(58), R(12), R(34), fadec, fb(mat4.translation(X(430), R(226), 0)));
  for (let i = 0; i < 9; i++) box(R(56), R(4), R(1.2), fadec, fb(mat4.translation(X(430), R(234), R(-15 + i * 3.75))));
  [-10, 0, 10].forEach(dz => cylinderX(X(459), X(466), R(3.2), 12, fadec, fb(mat4.translation(0, R(226), R(dz)))));
  add({
    id: 'fadec', material: 'metalDark', mesh: fadec,
    build: { at: 6.0, dur: 0.6, from: { t: radial(fadecTh, 0.45) } },
    explode: { at: 10.8, dur: 1.3, to: { t: v3.add(radial(fadecTh, 1.0), [-0.2, 0.3, 0]) } },
  });

  /* Plumbing and wiring: grows along its routes. */
  const route = (id: string, material: MaterialId, pts: Vec3[], r: number, at: number, lift: Vec3, clamp = 0.18) => add({
    id, material, grow: true, mesh: tube(pts, R(r), { radial: 8, samples: 8, clampEvery: clamp }),
    build: { at, dur: 1.1 }, explode: { at: 10.7 + (at - 6) * 0.3, dur: 1.3, to: { t: lift } },
  });
  route('fuelLine', 'copper', [
    at(392, 228, deg(198)), at(440, 222, deg(199)), at(472, 204, deg(196)), at(520, 150, deg(190)),
    at(560, 124, deg(182)), at(650, 118, deg(170)), at(700, 119, deg(160)), at(716, 119, deg(152)),
  ], 2.2, 6.0, [0.1, -0.55, 0.2]);
  route('oilSupply', 'metal', [
    at(392, 226, deg(176)), at(450, 218, deg(176)), at(480, 190, deg(174)), at(540, 118, deg(200)),
    at(700, 114, deg(210)), at(880, 130, deg(206)), at(986, 134, deg(200)),
  ], 1.5, 6.15, [0.2, -0.45, -0.3]);
  route('oilScavenge', 'metal', [
    at(386, 224, deg(184)), at(450, 212, deg(186)), at(500, 150, deg(188)), at(600, 110, deg(222)),
    at(820, 118, deg(226)), at(990, 136, deg(214)),
  ], 1.3, 6.25, [0.25, -0.5, -0.45]);
  route('bleedA', 'metal', [at(610, 94, deg(40)), at(636, 108, deg(28)), at(676, 128, deg(12)), at(720, 140, deg(2)), at(760, 146, deg(0))], 5, 6.3, [0, 0.6, 0.2], 0);
  route('bleedB', 'metal', [at(690, 88, deg(-40)), at(705, 104, deg(-28)), at(730, 126, deg(-12)), at(770, 142, deg(-2)), at(800, 146, deg(0))], 4.5, 6.35, [0, 0.6, -0.25], 0);
  route('harnessFan', 'harness', [
    at(466, 226, fadecTh), at(470, 219, deg(145)), at(460, 218, deg(165)), at(450, 218, deg(185)), at(470, 212, deg(205)),
    at(520, 150, deg(212)), at(600, 106, deg(215)), at(760, 116, deg(236)), at(900, 132, deg(240)),
  ], 1.8, 6.1, [0, -0.4, -0.35]);
  route('harnessCore', 'harness', [
    at(466, 226, deg(124)), at(470, 220, deg(118)), at(480, 212, deg(105)), at(520, 150, deg(100)),
    at(580, 104, deg(118)), at(700, 112, deg(130)), at(850, 118, deg(128)), at(980, 134, deg(122)),
  ], 1.6, 6.4, [0, 0.1, 0.55]);
  const egt: Vec3[] = [];
  for (let i = 0; i <= 12; i++) egt.push(at(858, 112, deg(115 + (i * 250) / 12)));
  route('harnessEgt', 'harness', egt, 1.2, 6.5, [0.9, 0.35, 0], 0.12);
  const probes = new MeshBuilder();
  for (let i = 0; i < 8; i++) {
    const th = deg(125 + (i * 235) / 7);
    const m = mat4.multiply(mat4.rotationX(th), mat4.multiply(mat4.translation(X(858), R(96), 0), mat4.rotationZ(Math.PI / 2)));
    cylinderX(0, R(16), R(1.4), 8, probes, m);
    cylinderX(R(14), R(18), R(3), 8, probes, m);
  }
  [[520, 98, deg(150)], [760, 108, deg(140)], [560, 120, deg(200)]].forEach(([x, r, th]) => {
    const m = mat4.multiply(mat4.rotationX(th), mat4.multiply(mat4.translation(X(x), R(r), 0), mat4.rotationZ(Math.PI / 2)));
    cylinderX(0, R(12), R(2), 10, probes, m);
  });
  add({
    id: 'probes', material: 'metal', mesh: probes, minor: true,
    build: { at: 6.6, dur: 0.5, from: { t: [0, 0.1, 0] } },
    explode: { at: 12.9, dur: 1.3, to: { t: [0.95, 0.3, 0] } },
  });
  const mounts = new MeshBuilder();
  box(R(26), R(46), R(40), mounts, mat4.translation(X(450), R(238), 0));
  box(R(10), R(20), R(70), mounts, mat4.translation(X(450), R(262), 0));
  box(R(22), R(34), R(30), mounts, mat4.translation(X(988), R(145), 0));
  [-1, 1].forEach(s => {
    const a = [X(980), R(128), s * R(18)] as Vec3;
    const b = [X(996), R(158), s * R(26)] as Vec3;
    cylinderX(0, v3.len(v3.sub(b, a)), R(3), 10, mounts, orient(a, v3.sub(b, a)));
  });
  add({
    id: 'mounts', material: 'metalDark', mesh: mounts,
    build: { at: 6.7, dur: 0.6, from: { t: [0, 0.4, 0] } },
    explode: { at: 10.6, dur: 1.3, to: { t: [0, 0.7, 0] } },
  });

  /* Nacelle: core cowl halves, thrust reverser C-ducts + cascades, fan cowl doors, inlet. */
  const coreCowlProf = P([
    ...bezier2([472, 120], [560, 132], [680, 138], [770, 138], 10),
    ...bezier2([770, 138], [860, 138], [940, 134], [1002, 130], 8).slice(1),
    ...bezier2([1002, 127], [940, 131], [860, 135], [770, 135], 8),
    ...bezier2([770, 135], [680, 135], [560, 129], [476, 117], 10).slice(1),
  ]);
  const hTop = (r: number): [number, number] => [R(r), 0];
  add({
    id: 'coreCowlA', material: 'composite', mesh: lathe(coreCowlProf, { closed: true, seg: 96, a0: 0, a1: Math.PI, sharp: 50 }),
    hinge: hTop(138), build: { at: 6.9, dur: 0.8, from: { hinge: -1.2 } },
    explode: { at: 10.5, dur: 1.3, to: { hinge: -0.2, t: [0.9, -0.3, 0.75] } },
  });
  add({
    id: 'coreCowlB', material: 'composite', mesh: lathe(coreCowlProf, { closed: true, seg: 96, a0: Math.PI, a1: 2 * Math.PI, sharp: 50 }),
    hinge: hTop(138), build: { at: 6.95, dur: 0.8, from: { hinge: 1.2 } },
    explode: { at: 10.55, dur: 1.3, to: { hinge: 0.2, t: [0.9, 0.5, -0.75] } },
  });
  add({
    id: 'cascades', material: 'metalDark',
    mesh: (() => {
      const m = new MeshBuilder();
      lathe(rect(450, 524, 204, 206), { closed: true, seg: 96, ...CUT }, m);
      lathe(rect(450, 524, 219, 221), { closed: true, seg: 96, ...CUT }, m);
      for (let i = 0; i < 4; i++) {
        bladeRing(B({ x: 454 + i * 17, rHub: 206, rTip: 219, chordHub: 8, chordTip: 8, staggerHub: deg(-35), staggerTip: deg(-35), thick: 0.08, camber: -0.1, spanSeg: 1, chordSeg: 3 }), 64, 0, m);
      }
      return m;
    })(),
    minor: true,
    build: { at: 6.7, dur: 0.6 }, explode: { at: 10.3, dur: 1.3, to: { t: [1.2, 0.35, 0] } },
  });
  // Nacelle shells are grey (composite) on every face; a separate gold
  // "skin" overlays only the outward-facing surface, like the livery. Each
  // skin copies its shell's motion so they move, hinge and dissolve as one.
  const SKIN_LIFT = 0.6;
  const skinOf = (shell: PartDef, outer: P2[], arc: { a0: number; a1: number }) => add({
    ...shell,
    id: `${shell.id}Skin`,
    material: 'livery',
    mesh: lathe(P(outer.map(([x, r]) => [x, r + SKIN_LIFT] as P2)), { seg: 96, sharp: 50, edges: false, ...arc }),
  });
  const halfA = { a0: 0, a1: Math.PI };
  const halfB = { a0: Math.PI, a1: 2 * Math.PI };

  const trOuter = bezier2([442, 247], [560, 247], [690, 222], [772, 196], 12);
  const tr = P([
    ...trOuter,
    [772, 193],
    ...bezier2([772, 193], [700, 194], [560, 199], [442, 202], 12).slice(1),
  ]);
  skinOf(add({
    id: 'reverserA', material: 'composite', mesh: lathe(tr, { closed: true, seg: 96, ...halfA, sharp: 50 }),
    hinge: hTop(247), build: { at: 7.0, dur: 0.9, from: { hinge: -1.0, t: [0.5, 0, 0] } },
    explode: { at: 10.2, dur: 1.5, to: { hinge: -0.25, t: [2.1, -0.45, 1.05] } },
  }), trOuter, halfA);
  skinOf(add({
    id: 'reverserB', material: 'composite', mesh: lathe(tr, { closed: true, seg: 96, ...halfB, sharp: 50 }),
    hinge: hTop(247), build: { at: 7.05, dur: 0.9, from: { hinge: 1.0, t: [0.5, 0, 0] } },
    explode: { at: 10.25, dur: 1.5, to: { hinge: 0.25, t: [2.1, 0.6, -1.05] } },
  }), trOuter, halfB);
  const cowlOuter = bezier2([272, 244], [330, 248], [390, 248.5], [442, 247], 10);
  const cowl = P([
    ...cowlOuter,
    ...bezier2([442, 243], [390, 244.5], [330, 244], [272, 240], 10),
  ]);
  skinOf(add({
    id: 'fanCowlA', material: 'composite', mesh: lathe(cowl, { closed: true, seg: 96, ...halfA, sharp: 50 }),
    hinge: hTop(248), build: { at: 7.2, dur: 0.8, from: { hinge: -1.3 } },
    explode: { at: 10.0, dur: 1.3, to: { hinge: -0.3, t: [-0.45, -0.35, 0.95] } },
  }), cowlOuter, halfA);
  skinOf(add({
    id: 'fanCowlB', material: 'composite', mesh: lathe(cowl, { closed: true, seg: 96, ...halfB, sharp: 50 }),
    hinge: hTop(248), build: { at: 7.2, dur: 0.8, from: { hinge: 1.3 } },
    explode: { at: 10.05, dur: 1.3, to: { hinge: 0.3, t: [-0.45, 0.55, -0.95] } },
  }), cowlOuter, halfB);
  const inletOuter = bezier2([174, 214], [180, 232], [220, 242], [272, 244], 10);
  const inletProf = P([
    [272, 244], [272, 193], [205, 194],
    ...bezier2([205, 194], [182, 194], [170, 202], [174, 214], 8).slice(1),
    ...inletOuter.slice(1, -1),
  ]);
  const inlet = new MeshBuilder();
  lathe(inletProf, { closed: true, seg: 96, sharp: 40 }, inlet);
  // The forward-facing lip stays grey: gold starts aft of the leading edge.
  skinOf(add({
    id: 'inlet', material: 'composite', mesh: inlet,
    build: { at: 7.3, dur: 0.7, from: { t: [-0.9, 0, 0] } },
    explode: { at: 10.0, dur: 1.4, to: { t: [-1.55, 0, 0] } },
  }), inletOuter.filter(([x]) => x >= 200), { a0: 0, a1: 2 * Math.PI });
  add({
    id: 'inletLip', material: 'metal',
    mesh: lathe(P(bezier2([196, 194.5], [180, 195], [171, 203], [175, 216], 8)), { seg: 96, sharp: 50 }),
    build: { at: 7.3, dur: 0.7, from: { t: [-0.9, 0, 0] } },
    explode: { at: 10.0, dur: 1.4, to: { t: [-1.55, 0, 0] } },
  });

  return parts;
}

/** Checked by the unit test; keeps integrated GPUs comfortably at 60fps. */
export const TRIANGLE_BUDGET = 220_000;
